import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import ProtectedRoute from './components/ProtectedRoute'
import Sidebar from './components/Sidebar'
import Header from './components/Header'
import BottomNavigation from './components/BottomNavigation'
import LoginPage from './pages/LoginPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import ChangePasswordPage from './pages/ChangePasswordPage'
import DashboardPage from './pages/DashboardPage'
import VehiclesPage from './pages/VehiclesPage'
import ClientsPage from './pages/ClientsPage'
import ContractsPage from './pages/ContractsPage'
import NotificationsPage from './pages/NotificationsPage'
import IntegrationsPage from './pages/IntegrationsPage'
import Cobrancas from './pages/cobrancas'
import ContasReceberPage from './pages/ContasReceberPage';
import ContasPagarPage from './pages/ContasPagarPage';
import ReportsPage from './pages/ReportsPage';
import PredictionsPage from './pages/PredictionsPage';
import QuotesPage from './pages/QuotesPage';

// Páginas de Contratos
import ContractEditorPage from './pages/ContractEditorPage';
import ContractClausesLibraryPage from './pages/ContractClausesLibraryPage';

// Páginas de Cadastros
import BankAccountsPage from './pages/BankAccountsPage';
import SuppliersPage from './pages/SuppliersPage';
import PaymentMethodsPage from './pages/PaymentMethodsPage';
import FinancialClassesPage from './pages/FinancialClassesPage';
import MaintenanceTypesPage from './pages/MaintenanceTypesPage';
import VehicleTypesPage from './pages/VehicleTypesPage';
import SystemSettingsPage from './pages/SystemSettingsPage';
import DocumentTypesPage from './pages/DocumentTypesPage';
import CancellationReasonsPage from './pages/CancellationReasonsPage';
import ContractSettingsPage from './pages/ContractSettingsPage';
import InspectionTemplatesPage from './pages/InspectionTemplatesPage';

// Páginas de Fluxo de Caixa
import CashFlowDashboard from './pages/CashFlowDashboard';
import CashFlowTransactions from './pages/CashFlowTransactions';
import CashFlowBanks from './pages/CashFlowBanks';
import CashFlowCategories from './pages/CashFlowCategories';
import CashFlowTransfers from './pages/CashFlowTransfers';
import CashFlowReports from './pages/CashFlowReports';

// Páginas de Manutenção Preditiva
import PredictiveMaintenancePage from './pages/PredictiveMaintenancePage';
import MaintenanceConfigPage from './pages/MaintenanceConfigPage';
import VehicleMaintenanceSetup from './pages/VehicleMaintenanceSetup';

// Páginas de Rastreamento GPS e Motoristas
import TrackingPage from './pages/TrackingPage';
import FleetDashboardPage from './pages/FleetDashboardPage';
import GpsReportsPage from './pages/GpsReportsPage';
import OBDPage from './pages/OBDPage';
import DriversPage from './pages/DriversPage';

// Página de Gestão de Combustível
import FuelManagementPage from './pages/FuelManagementPage';

// Página de Compliance
import CompliancePage from './pages/CompliancePage';

// Página de Gestão de Pneus
import TiresPage from './pages/TiresPage';

// Página de Inspeções Unificadas
import InspectionsPage from './pages/InspectionsPage';

// Página de Equipamentos Auxiliares
import AuxiliaryEquipmentPage from './pages/AuxiliaryEquipmentPage';
import EquipmentCategoriesPage from './pages/EquipmentCategoriesPage';

// Página de Equipamentos Especiais
import EquipamentosEspeciaisPage from './pages/EquipamentosEspeciaisPage';

// Página de Política de Crédito
import CreditPolicyConfigPage from './pages/CreditPolicyConfigPage';

// Páginas de Faturas
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailsPage from './pages/InvoiceDetailsPage';
import NewInvoicePage from './pages/NewInvoicePage';
import ContractInvoicesPage from './pages/ContractInvoicesPage';

// Página de Gestão de Usuários
import UsersManagementPage from './pages/UsersManagementPage';
import UserRolesPage from './pages/UserRolesPage';

// Página de Email Analytics
import EmailAnalyticsPage from './pages/EmailAnalyticsPage';

// Página de Alertas Inteligentes
import IntelligentAlertsPage from './pages/IntelligentAlertsPage';

// Páginas de Marketing & CRM B2B
import MarketingDashboardPage from './pages/MarketingDashboardPage';
import MarketingLeadsPage from './pages/MarketingLeadsPage';
import MarketingPipelinePage from './pages/MarketingPipelinePage';
import MarketingContentPage from './pages/MarketingContentPage';
import MarketingWhatsappPage from './pages/MarketingWhatsappPage';
import MarketingCampaignsPage from './pages/MarketingCampaignsPage';
import MarketingAutomationPage from './pages/MarketingAutomationPage';
import MarketingCalendarPage from './pages/MarketingCalendarPage';
import MarketingAIPage from './pages/MarketingAIPage';
import MarketingIntegrationsPage from './pages/MarketingIntegrationsPage';
import MarketingAnalyticsPage from './pages/MarketingAnalyticsPage';

// Portal do Cliente
import ClientPortalPage from './pages/ClientPortalPage';

import './App.css'

function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Recuperar estado do sidebar do localStorage
  useEffect(() => {
    const collapsed = localStorage.getItem('sidebarCollapsed') === 'true'
    setSidebarCollapsed(collapsed)
  }, [])

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed
    setSidebarCollapsed(newState)
    localStorage.setItem('sidebarCollapsed', newState.toString())
  }

  const toggleSidebarMobile = () => {
    setSidebarOpen(!sidebarOpen)
  }

  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'var(--card-bg)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)'
                }
              }}
            />
            
            <Routes>
              {/* Rota de Login */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />

              {/* Portal do Cliente */}
              <Route path="/portal/client" element={
                <ProtectedRoute>
                  <ClientPortalPage />
                </ProtectedRoute>
              } />

              {/* Rotas Protegidas */}
              <Route path="/*" element={
                <ProtectedRoute>
                  <MainLayout
                    sidebarCollapsed={sidebarCollapsed}
                    sidebarOpen={sidebarOpen}
                    toggleSidebar={toggleSidebar}
                    toggleSidebarMobile={toggleSidebarMobile}
                    setSidebarOpen={setSidebarOpen}
                  />
                </ProtectedRoute>
              } />
            </Routes>
          </div>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  )
}

// Layout principal do sistema
function MainLayout({ sidebarCollapsed, sidebarOpen, toggleSidebar, toggleSidebarMobile, setSidebarOpen }) {
  return (
    <>
      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        open={sidebarOpen}
        onToggle={toggleSidebar}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Overlay para mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleSidebarMobile}
        />
      )}

      {/* Conteúdo Principal */}
      <div className={`
        transition-all duration-300 ease-in-out
        ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}
        min-h-screen flex flex-col
      `}>
        {/* Header */}
        <Header
          onToggleSidebar={toggleSidebar}
          onToggleSidebarMobile={toggleSidebarMobile}
        />

        {/* Main Content - pb-20 para não ficar sob o bottom nav em mobile */}
        <main className="flex-1 p-4 lg:p-6 pb-20 lg:pb-6">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/vehicles" element={<VehiclesPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/contracts" element={<ContractsPage />} />
            <Route path="/contracts/new" element={<ContractEditorPage />} />
            <Route path="/contracts/:id/edit" element={<ContractEditorPage />} />
            <Route path="/contracts/:id/invoices" element={<ContractInvoicesPage />} />
            <Route path="/contracts/clauses" element={<ContractClausesLibraryPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/cobrancas" element={<Cobrancas />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/invoices/new" element={<NewInvoicePage />} />
            <Route path="/invoices/:id" element={<InvoiceDetailsPage />} />
            <Route path="/receivables" element={<ContasReceberPage />} />
            <Route path="/payables" element={<ContasPagarPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/predictions" element={<PredictionsPage />} />
            <Route path="/quotes" element={<QuotesPage />} />

            {/* Rotas de Cadastros */}
            <Route path="/cadastros/contas-bancarias" element={<BankAccountsPage />} />
            <Route path="/cadastros/fornecedores" element={<SuppliersPage />} />
            <Route path="/cadastros/formas-pagamento" element={<PaymentMethodsPage />} />
            <Route path="/cadastros/classes-financeiras" element={<FinancialClassesPage />} />
            <Route path="/cadastros/tipos-manutencao" element={<MaintenanceTypesPage />} />
            <Route path="/cadastros/tipos-veiculo" element={<VehicleTypesPage />} />
            <Route path="/cadastros/tipos-documento" element={<DocumentTypesPage />} />
            <Route path="/cadastros/motivos-cancelamento" element={<CancellationReasonsPage />} />
            <Route path="/configuracoes" element={<SystemSettingsPage />} />
            <Route path="/contract-settings" element={<ContractSettingsPage />} />

            {/* Rotas de Fluxo de Caixa */}
            <Route path="/cash-flow" element={<CashFlowDashboard />} />
            <Route path="/cash-flow/transactions" element={<CashFlowTransactions />} />
            <Route path="/cash-flow/banks" element={<CashFlowBanks />} />
            <Route path="/cash-flow/categories" element={<CashFlowCategories />} />
            <Route path="/cash-flow/transfers" element={<CashFlowTransfers />} />
            <Route path="/cash-flow/reports" element={<CashFlowReports />} />

            {/* Rotas de Manutenção Preditiva */}
            <Route path="/predictive-maintenance" element={<PredictiveMaintenancePage />} />
            <Route path="/vehicles/:vehicleId/maintenance-setup" element={<VehicleMaintenanceSetup />} />
            <Route path="/maintenance-config" element={<MaintenanceConfigPage />} />

            {/* Rotas de Rastreamento GPS e Motoristas */}
            <Route path="/tracking" element={<TrackingPage />} />
            <Route path="/fleet-dashboard" element={<FleetDashboardPage />} />
            <Route path="/gps-reports" element={<GpsReportsPage />} />
            <Route path="/obd" element={<OBDPage />} />
            <Route path="/drivers" element={<DriversPage />} />

            {/* Rota de Gestão de Combustível */}
            <Route path="/fuel" element={<FuelManagementPage />} />

            {/* Rota de Compliance */}
            <Route path="/compliance" element={<CompliancePage />} />

            {/* Rota de Gestão de Pneus */}
            <Route path="/tires" element={<TiresPage />} />

            {/* Rota de Inspeções Unificadas */}
            <Route path="/inspections" element={<InspectionsPage />} />
            <Route path="/settings/inspection-templates" element={<InspectionTemplatesPage />} />

            {/* Rota de Equipamentos Auxiliares */}
            <Route path="/auxiliary-equipment" element={<AuxiliaryEquipmentPage />} />
            <Route path="/equipment-categories" element={<EquipmentCategoriesPage />} />

            {/* Rota de Equipamentos Especiais */}
            <Route path="/equipamentos-especiais" element={<EquipamentosEspeciaisPage />} />

            {/* Rota de Política de Crédito */}
            <Route path="/credit-policy" element={<CreditPolicyConfigPage />} />

            {/* Rota de Gestão de Usuários */}
            <Route path="/users-management" element={<UsersManagementPage />} />
            <Route path="/user-roles" element={<UserRolesPage />} />

            {/* Rota de Email Analytics */}
            <Route path="/email-analytics" element={<EmailAnalyticsPage />} />

            {/* Rota de Alertas Inteligentes */}
            <Route path="/intelligent-alerts" element={<IntelligentAlertsPage />} />

            {/* Rotas de Marketing & CRM B2B */}
            <Route path="/marketing" element={<MarketingDashboardPage />} />
            <Route path="/marketing/leads" element={<MarketingLeadsPage />} />
            <Route path="/marketing/pipeline" element={<MarketingPipelinePage />} />
            <Route path="/marketing/content" element={<MarketingContentPage />} />
            <Route path="/marketing/whatsapp" element={<MarketingWhatsappPage />} />
            <Route path="/marketing/campaigns" element={<MarketingCampaignsPage />} />
            <Route path="/marketing/automation" element={<MarketingAutomationPage />} />
            <Route path="/marketing/calendar" element={<MarketingCalendarPage />} />
            <Route path="/marketing/ia" element={<MarketingAIPage />} />
            <Route path="/marketing/integrations" element={<MarketingIntegrationsPage />} />
            <Route path="/marketing/analytics" element={<MarketingAnalyticsPage />} />
          </Routes>
        </main>
      </div>

      {/* Bottom Navigation para Mobile */}
      <BottomNavigation onMenuClick={toggleSidebarMobile} />
    </>
  )
}

export default App